import { useState } from 'react';
import { Bot, X } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useOrdersSignalR } from '@/features/orders/hooks/useOrdersSignalR';
import { useAiUnits } from '../hooks/useBilling';
import { useTenantEntitlements } from '../hooks/useTenantEntitlements';
import { TENANT_FEATURE_KEYS } from '../types/billing.types';
import type { AiUnitActiveAlert } from '../types/billing.types';
import {
  AI_UNITS_ALERT_TEXT,
  AI_UNITS_TEAM_TEXT,
  cycleKey,
  dismissAlert,
  isAlertDismissed,
  isDismissibleAlert,
} from '../lib/ai-units';
import { AiUnitsPurchaseDialog } from './AiUnitsPurchaseDialog';
import styles from './AiUnitsBanner.module.css';

/**
 * Faixa global de atendimentos no `PanelLayout` (change billing-ai-units), ao
 * lado da `PrintingStatusBar`, em duas variantes:
 *
 * - **dono e gerente** (`billing.read`): o aviso ativo de
 *   `GET /billing/ai-units` (80%, 100% ou sem saldo), só com o plano com
 *   Atendente IA. 80% e 100% se dispensam até o ciclo seguinte; o sem saldo,
 *   não. O botão de compra é de quem tem `billing.write`; o gerente lê
 *   "Peça ao dono para comprar". Com a venda desligada no ambiente
 *   (`canPurchase = false`), nem um nem outro: o aviso fica sem compra.
 * - **equipe** (`conversations.read` sem `billing.read`): só o sem saldo, lido
 *   do status do atendente (`GET /customer-agent/status`, `state = no_units`,
 *   pelo `useCustomerAgentStatus` de `customer-agent-handoff`), sem números,
 *   sem botão e sem chamar `/billing/ai-units`. O texto já está em
 *   `AiUnitsTeamNotice`; a ligação com o hook entra quando o módulo do
 *   atendente chegar ao painel, e até lá a equipe não vê faixa nenhuma.
 */
export function AiUnitsBanner() {
  const permissions = useAuthStore((s) => s.user?.permissions);
  const has = (permission: string) => permissions?.includes(permission) ?? false;

  if (has('billing.read')) {
    return <OwnerBanner canBuy={has('billing.write')} canReceiveRealtime={has('orders.read')} />;
  }
  return null;
}

/**
 * Sem o recurso do plano nos direitos, nenhuma leitura da carteira: a faixa
 * vive em todas as telas, e a loja do Essencial não paga por ela.
 */
function OwnerBanner(props: { canBuy: boolean; canReceiveRealtime: boolean }) {
  const { hasFeature } = useTenantEntitlements();
  if (!hasFeature(TENANT_FEATURE_KEYS.whatsappAttendant)) return null;
  return <OwnerBannerContent {...props} />;
}

/**
 * Mantém a conexão de tempo real aberta em qualquer tela: o
 * `billing.ai_units_updated` relê o saldo, e a faixa aparece na hora. O hub
 * só põe no grupo do tenant quem vê pedidos (o dono e o gerente veem).
 */
function AiUnitsRealtimeConnection() {
  useOrdersSignalR();
  return null;
}

function OwnerBannerContent({ canBuy, canReceiveRealtime }: { canBuy: boolean; canReceiveRealtime: boolean }) {
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const { data: summary } = useAiUnits();
  const [dismissedNow, setDismissedNow] = useState<string[]>([]);
  const [buying, setBuying] = useState(false);

  const alert: AiUnitActiveAlert | null = summary?.hasAttendantPlan ? summary.alert : null;
  // Com a venda desligada no ambiente, o aviso continua, sem botão nem "Peça ao dono": não há o que comprar.
  const salesOpen = summary?.canPurchase === true;
  const showBuy = canBuy && salesOpen;
  const cycle = cycleKey(summary?.cycle ?? null);
  const dismissed = !!alert
    && (dismissedNow.includes(`${cycle}:${alert}`) || isAlertDismissed(tenantId, cycle, alert));

  const dismiss = (current: AiUnitActiveAlert) => {
    dismissAlert(tenantId, cycle, current);
    setDismissedNow((list) => [...list, `${cycle}:${current}`]);
  };

  return (
    <>
      {canReceiveRealtime && <AiUnitsRealtimeConnection />}

      {alert && !dismissed && (
        <div
          className={alert === 'exhausted' ? styles.exhausted : styles.warning}
          role={alert === 'exhausted' ? 'alert' : 'status'}
          data-testid="ai-units-banner"
        >
          <div className={styles.message}>
            <Bot size={16} className={styles.icon} aria-hidden="true" />
            <span className={styles.text}>{AI_UNITS_ALERT_TEXT[alert]}</span>
            {showBuy ? (
              <button type="button" className={styles.action} onClick={() => setBuying(true)}>
                Comprar atendimentos
              </button>
            ) : salesOpen ? (
              <span className={styles.hint}>Peça ao dono para comprar</span>
            ) : null}
          </div>
          {isDismissibleAlert(alert) && (
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => dismiss(alert)}
              aria-label="Dispensar aviso de atendimentos até o próximo ciclo"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
      )}

      {showBuy && <AiUnitsPurchaseDialog open={buying} onClose={() => setBuying(false)} />}
    </>
  );
}

/**
 * A variante da equipe: "sem saldo" sem número, sem botão de compra. Quem
 * decide se aparece é quem lê o status do atendente (`state = no_units`).
 */
export function AiUnitsTeamNotice() {
  return (
    <div className={styles.exhausted} role="alert" data-testid="ai-units-team-banner">
      <div className={styles.message}>
        <Bot size={16} className={styles.icon} aria-hidden="true" />
        <span className={styles.text}>{AI_UNITS_TEAM_TEXT}</span>
      </div>
    </div>
  );
}
